import type { Plugin } from "chart.js";

import type { ClinicalPhaseZone } from "../TargetChart.types";

const PHASE_COLORS: Record<ClinicalPhaseZone["phase"], string> = {
  baseline: "#E8EAF6",
  intervention: "#E8F5E9",
  generalization: "#F3E5F5",
  maintenance: "#E0F7FA",
};

/**
 * Draws the clinical phase background bands behind the series: a tinted block
 * per phase, a divider at each boundary, and the phase name below the axis.
 *
 * @param phaseZones   - Bands to paint, as index ranges into the labels
 * @param dividerColor - Stroke color for the phase boundaries
 * @param fontFamily   - Font used for the phase labels
 * @param labelColor   - Fill color for the phase labels
 */
export function buildPhaseZonePlugin(
  phaseZones: ClinicalPhaseZone[],
  dividerColor: string,
  fontFamily: string,
  labelColor: string
): Plugin<"line"> {
  return {
    id: "targetChartPhaseZones",
    beforeDraw(chart) {
      if (!phaseZones.length) return;
      const { ctx, scales, chartArea } = chart;
      const xScale = scales["x"];
      if (!xScale || !chartArea) return;

      ctx.save();

      phaseZones.forEach((zone) => {
        const startX = xScale.getPixelForTick(zone.startIndex);
        const endX = xScale.getPixelForTick(zone.endIndex);
        const width = endX - startX;

        // Background fill
        ctx.fillStyle = PHASE_COLORS[zone.phase] ?? "#F5F5F5";
        ctx.globalAlpha = 0.5;
        ctx.fillRect(startX, chartArea.top, width, chartArea.bottom - chartArea.top);
        ctx.globalAlpha = 1;

        // Phase divider lines (left boundary)
        if (zone.startIndex > 0) {
          ctx.strokeStyle = dividerColor;
          ctx.lineWidth = 1;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(startX, chartArea.top);
          ctx.lineTo(startX, chartArea.bottom);
          ctx.stroke();
        }

        // Phase label at the bottom center of the zone
        const centerX = startX + width / 2;
        ctx.fillStyle = labelColor;
        ctx.font = `400 11px ${fontFamily}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(zone.label, centerX, chartArea.bottom + 24);
      });

      ctx.restore();
    },
  };
}
